import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp } from "../../api/auth_api";
import { CircularProgress } from "@mui/material";
import { Eye, EyeOff } from "lucide-react";

const passwordRules = {
    length: (v) => v.length >= 8,
    upper: (v) => /[A-Z]/.test(v),
    lower: (v) => /[a-z]/.test(v),
    number: (v) => /\d/.test(v),
    special: (v) => /[@$!%*?&#]/.test(v),
};

const CandlestickChartBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let candles = [];
        let maData = [];
        let volumeData = [];
        let signals = [];
        let scrollOffset = 0;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        const generateCandle = (prevClose) => {
            const open = prevClose + (Math.random() - 0.5) * 15;
            const close = open + (Math.random() - 0.5) * 20;
            const high = Math.max(open, close) + Math.random() * 10;
            const low = Math.min(open, close) - Math.random() * 10;
            const volume = Math.random() * 50 + 20;

            return { open, high, low, close, volume };
        };

        let lastClose = 300;
        const candleCount = Math.ceil(canvas.width / 25) + 10;

        for (let i = 0; i < candleCount; i++) {
            const candle = generateCandle(lastClose);
            candles.push(candle);
            volumeData.push(candle.volume);
            lastClose = candle.close;

            if (i >= 19) {
                const sum = candles.slice(i - 19, i + 1).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);
            } else {
                maData.push(null);
            }

            if (i > 20 && Math.random() > 0.92) {
                signals.push({
                    index: i,
                    type: Math.random() > 0.5 ? 'buy' : 'sell',
                    price: candle.close
                });
            }
        }

        const drawCandle = (x, candle, width) => {
            const bullish = candle.close > candle.open;
            const bodyTop = Math.min(candle.open, candle.close);
            const bodyBottom = Math.max(candle.open, candle.close);
            const bodyHeight = Math.abs(candle.close - candle.open);

            ctx.strokeStyle = bullish ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x + width / 2, candle.high);
            ctx.lineTo(x + width / 2, candle.low);
            ctx.stroke();

            ctx.fillStyle = bullish ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.fillRect(x, bodyTop, width - 2, Math.max(bodyHeight, 2));
        };

        const drawVolume = (x, volume, width, maxVolume) => {
            const volumeHeight = (volume / maxVolume) * 80;
            ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
            ctx.fillRect(x, canvas.height - volumeHeight - 10, width - 2, volumeHeight);
        };

        const drawSignal = (x, y, type) => {
            ctx.save();
            ctx.fillStyle = type === 'buy' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.strokeStyle = type === 'buy' ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 1)';
            ctx.lineWidth = 2;

            ctx.beginPath();
            if (type === 'buy') {
                ctx.moveTo(x, y + 15);
                ctx.lineTo(x - 8, y + 5);
                ctx.lineTo(x + 8, y + 5);
            } else {
                ctx.moveTo(x, y - 15);
                ctx.lineTo(x - 8, y - 5);
                ctx.lineTo(x + 8, y - 5);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = 'white';
            ctx.font = 'bold 10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(type.toUpperCase(), x, y + (type === 'buy' ? 30 : -20));
            ctx.restore();
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const candleWidth = 20;
            const spacing = 25;
            const chartHeight = canvas.height - 120;

            const visibleCandles = candles.slice(0, candleCount);
            const allPrices = visibleCandles.flatMap(c => [c.high, c.low]);
            const minPrice = Math.min(...allPrices) - 20;
            const maxPrice = Math.max(...allPrices) + 20;
            const priceRange = maxPrice - minPrice;

            const scaleY = (price) => {
                return 40 + ((maxPrice - price) / priceRange) * (chartHeight - 80);
            };

            const maxVolume = Math.max(...volumeData);

            ctx.strokeStyle = 'rgba(100, 116, 139, 0.1)';
            ctx.lineWidth = 1;
            for (let i = 0; i < 5; i++) {
                const y = 40 + (chartHeight - 80) * (i / 4);
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            ctx.strokeStyle = 'rgba(234, 179, 8, 0.6)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            let maStarted = false;

            candles.forEach((candle, i) => {
                if (maData[i] !== null) {
                    const x = (i * spacing - scrollOffset) + candleWidth / 2;
                    const y = scaleY(maData[i]);

                    if (x > -spacing && x < canvas.width + spacing) {
                        if (!maStarted) {
                            ctx.moveTo(x, y);
                            maStarted = true;
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                }
            });
            ctx.stroke();

            candles.forEach((candle, i) => {
                const x = i * spacing - scrollOffset;

                if (x > -spacing && x < canvas.width + spacing) {
                    const scaledCandle = {
                        open: scaleY(candle.open),
                        high: scaleY(candle.high),
                        low: scaleY(candle.low),
                        close: scaleY(candle.close)
                    };

                    drawCandle(x, scaledCandle, candleWidth);
                    drawVolume(x, volumeData[i], candleWidth, maxVolume);
                }
            });

            signals.forEach(signal => {
                const x = signal.index * spacing - scrollOffset + candleWidth / 2;
                const y = scaleY(signal.price);

                if (x > 0 && x < canvas.width) {
                    drawSignal(x, y, signal.type);
                }
            });

            scrollOffset += 0.5;

            if (scrollOffset >= spacing) {
                scrollOffset -= spacing;

                candles.shift();
                maData.shift();
                volumeData.shift();
                signals = signals.map(s => ({ ...s, index: s.index - 1 })).filter(s => s.index >= 0);

                const newCandle = generateCandle(candles[candles.length - 1].close);
                candles.push(newCandle);
                volumeData.push(newCandle.volume);

                const sum = candles.slice(-20).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);

                if (Math.random() > 0.95) {
                    signals.push({
                        index: candles.length - 1,
                        type: Math.random() > 0.5 ? 'buy' : 'sell',
                        price: newCandle.close
                    });
                }
            }

            ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
            ctx.fillRect(10, 10, 250, 60);
            ctx.strokeStyle = 'rgba(100, 116, 139, 0.5)';
            ctx.strokeRect(10, 10, 250, 60);

            ctx.font = '12px Arial';
            ctx.fillStyle = 'rgba(148, 163, 184, 1)';
            ctx.textAlign = 'left';
            ctx.fillText('Live Market Simulation', 20, 30);

            ctx.fillStyle = 'rgba(34, 197, 94, 1)';
            ctx.fillText('● Green = Bullish', 20, 50);
            ctx.fillStyle = 'rgba(239, 68, 68, 1)';
            ctx.fillText('● Red = Bearish', 150, 50);

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.5 }}
        />
    );
};

const Signup = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        full_name: "",
        user_name: "",
        user_email: "",
        user_password: "",
        confirm_password: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [confirmFocused, setConfirmFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        setFormData((p) => ({
            ...p,
            [e.target.name]: e.target.value,
        }));
    };

    const passwordChecks = {
        length: passwordRules.length(formData.user_password),
        upper: passwordRules.upper(formData.user_password),
        lower: passwordRules.lower(formData.user_password),
        number: passwordRules.number(formData.user_password),
        special: passwordRules.special(formData.user_password),
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);
    const isPasswordMatch =
        formData.user_password &&
        formData.user_password === formData.confirm_password;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            setError("Password does not meet all requirements.");
            return;
        }

        if (!isPasswordMatch) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        setError(null);

        const payload = {
            full_name: formData.full_name,
            user_name: formData.user_email,
            user_email: formData.user_email,
            user_password: formData.user_password,
        };

        const res = await signUp(payload);
        console.log(res);

        if (res.success) {
            sessionStorage.setItem("authToken", res.data?.token || "");
            setSuccess(
                res.data?.response_status === "SUCCESS"
                    ? "A mail has been sent to your email. Please verify it to continue."
                    : res.data?.response_message || "Request completed successfully."
            );
            setError(false);
        } else {
            if (res.error === "ERROR: EmailId already exists") {
                setError("Email ID already exists. Try logging in instead.");
            } else {
                setError(res.error || "Signup failed. Please try again.");
            }

            setSuccess(false);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isPasswordValid) {
            setPasswordFocused(false);
        }
    }, [isPasswordValid]);

    const Rule = ({ ok, label }) => (
        <div className={`text-sm ${ok ? "text-green-400" : "text-slate-400"}`}>
            {ok ? "✔" : "•"} {label}
        </div>
    );

    return (
        <div className="min-h-screen relative flex items-center justify-center p-4 bg-[#0F172A] overflow-hidden">
            {/* Animated Candlestick Chart Background */}
            <CandlestickChartBackground />

            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

            {/* Signup Box */}
            <div className="relative w-full max-w-md z-10">
                <div className="bg-[#1E293B]/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 p-8 space-y-6 max-h-[90vh] overflow-y-auto">
                    {/* Logo */}
                    <div className="text-center">
                        <h1
                            className="font-bold text-3xl mb-2 cursor-pointer"
                            style={{ color: 'var(--color-primary)' }}
                            onClick={() => navigate("/landingPage")}
                        >
                            FactorAlpha
                        </h1>
                        <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full" />
                    </div>

                    {/* Heading */}
                    <div className="text-center">
                        <p className="font-semibold text-2xl mb-2 text-white">
                            Create New Account
                        </p>
                        <p className="text-sm text-slate-400">
                            Create an account to access real-time market data and premium analysis
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-sm border border-red-500/50">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="bg-green-500/20 text-green-400 p-3 rounded-lg text-sm border border-green-500/50">
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                            name="full_name"
                            placeholder="Full Name"
                            value={formData.full_name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />

                        <input
                            type="email"
                            name="user_email"
                            placeholder="Email"
                            value={formData.user_email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />

                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="user_password"
                                placeholder="Password"
                                value={formData.user_password}
                                onChange={handleChange}
                                onFocus={() => setPasswordFocused(true)}
                                required
                                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg pr-12 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />

                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                            </button>
                        </div>

                        {passwordFocused && (
                            <div className="grid grid-cols-1 gap-1 pl-1">
                                <Rule ok={passwordChecks.length} label="Minimum 8 characters" />
                                <Rule ok={passwordChecks.upper} label="One uppercase letter" />
                                <Rule ok={passwordChecks.lower} label="One lowercase letter" />
                                <Rule ok={passwordChecks.number} label="One number" />
                                <Rule ok={passwordChecks.special} label="One special character" />
                            </div>
                        )}

                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirm_password"
                                placeholder="Re-enter Password"
                                value={formData.confirm_password}
                                onChange={handleChange}
                                onFocus={() => setConfirmFocused(true)}
                                required
                                className={`w-full px-4 py-3 bg-slate-800/50 border rounded-lg pr-12 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formData.confirm_password && !isPasswordMatch
                                    ? "border-red-500"
                                    : "border-slate-700"
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword((v) => !v)}
                                className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                            </button>
                        </div>

                        {confirmFocused && formData.confirm_password && (
                            <div className={`text-sm pl-1 ${isPasswordMatch ? "text-green-400" : "text-red-400"}`}>
                                {isPasswordMatch ? "✔ Passwords match" : "✖ Passwords do not match"}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !isPasswordValid || !isPasswordMatch}
                            className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-blue-500/50"
                        >
                            {loading && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                            <span>{loading ? "Signing up..." : "Sign up"}</span>
                        </button>
                    </form>

                    <div className="text-center text-sm pt-4 border-t border-slate-700/50">
                        <span className="text-slate-400">Already have an account?{" "}</span>
                        <Link
                            to="/login"
                            className="font-medium text-blue-400 hover:underline transition-colors"
                        >
                            Login
                        </Link>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
            </div>
        </div>
    );
};

export default Signup;